import React, { useEffect, useState } from 'react'
import {
  CButton,
  CCloseButton,
  CCol,
  COffcanvas,
  COffcanvasBody,
  COffcanvasHeader,
  COffcanvasTitle,
  CFormInput,
  CFormLabel,
  CFormSelect,
  CLoadingButton,
  CRow,
} from '@coreui/react-pro'
import moment from 'moment'
import ToastComponent from 'src/components/common/TaostComponent'
import { ImportErrorAlert, InfoBox } from 'src/components/common/ImportWizard'
import PlayHQService from 'src/service/PlayHQService'
import { errorsOf } from './playhqShared'

const DAYS_BEFORE = [3, 2, 1]
export const roundDate = (value) => (value ? moment(value).format('ddd D MMM, h:mm a') : '')

/**
 * Right-side panel for one round's auto sync: how many days before the round starts its
 * not-started fixtures join in. Saving hands the page's refreshed settings to onSaved.
 */
const RoundSyncForm = ({ round, onClose, onSaved }) => {
  const [days, setDays] = useState('2')
  const [errors, setErrors] = useState([])
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (round) setDays(String(round.days_before))
    setErrors([])
  }, [round])

  const save = async () => {
    setSaving(true)
    setErrors([])
    try {
      const res = await PlayHQService.saveRoundSyncDays({
        round_id: round.id,
        days_before: Number(days),
      })
      if (!res.success) {
        setErrors(errorsOf(res))
        return
      }
      ToastComponent(res.message, 'success')
      onSaved(res.data)
      onClose()
    } catch (e) {
      setErrors(['The round could not be saved. Please try again.'])
    } finally {
      setSaving(false)
    }
  }

  const syncFrom = round && moment(round.start_date).subtract(Number(days), 'days')

  return (
    <COffcanvas placement="end" visible={!!round} onHide={onClose} backdrop style={{ width: 480 }}>
      <COffcanvasHeader>
        <div>
          <COffcanvasTitle>Round {round?.round} auto sync</COffcanvasTitle>
          <div className="text-medium-emphasis small">
            Choose when this round&apos;s fixtures start syncing from play.cricket.
          </div>
        </div>
        <CCloseButton className="text-reset" onClick={onClose} />
      </COffcanvasHeader>
      <COffcanvasBody className="d-flex flex-column">
        <ImportErrorAlert errors={errors} title="The round could not be saved:" />
        <CRow className="g-3 flex-grow-1 align-content-start">
          <CCol xs={6}>
            <CFormLabel htmlFor="round_start">Round starts</CFormLabel>
            <CFormInput id="round_start" value={roundDate(round?.start_date)} disabled />
          </CCol>
          <CCol xs={6}>
            <CFormLabel htmlFor="round_end">Round ends</CFormLabel>
            <CFormInput id="round_end" value={roundDate(round?.end_date)} disabled />
          </CCol>
          <CCol xs={12}>
            <CFormLabel htmlFor="days_before">Start syncing *</CFormLabel>
            <CFormSelect id="days_before" value={days} onChange={(e) => setDays(e.target.value)}>
              {DAYS_BEFORE.map((d) => (
                <option key={d} value={d}>
                  {d} day{d === 1 ? '' : 's'} before the round starts
                </option>
              ))}
            </CFormSelect>
          </CCol>
          <CCol xs={12}>
            <InfoBox className="mb-0">
              This round&apos;s fixtures start syncing on {syncFrom ? roundDate(syncFrom) : '-'}, on
              your sync days, and keep syncing until the next round starts.
            </InfoBox>
          </CCol>
        </CRow>
      </COffcanvasBody>
      <div className="d-flex justify-content-between gap-2 p-3 border-top">
        <CButton color="secondary" variant="outline" onClick={onClose}>
          Cancel
        </CButton>
        <CLoadingButton color="primary" loading={saving} onClick={save}>
          Save Changes
        </CLoadingButton>
      </div>
    </COffcanvas>
  )
}

export default RoundSyncForm
